import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Container,
  Feedback,
  Flex,
  HeadCellSortState,
  Link,
  Pagination,
  Panel,
  PanelStatus,
  SearchInputField,
  Text,
  theme,
  toggleSortStates,
  useMediaMax,
  usePagination,
  useTextInputField,
} from '@foradevelopment/fora-ui';
import { getReadablePayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';

import { useTypedTranslation } from '../../i18n/i18n';
import { EmployerMyPagesErrorPage, SearchFilterInfo } from '../../components';
import { PayrollReportingTable } from '../components/PayrollReportingTable';
import {
  goToPayrollInput,
  goToReviewAndConfirm,
  setPayAmountSummaryData,
  setPayInputErrors,
  setReviewAndConfirmChecked,
} from '../../redux/payroll/payrollSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  defaultPayrollTableSortState,
  PayrollTableSortId,
  PayrollTableSortState,
} from '../components/PayrollReportingTable/PayrollTableSortState';
import { filterListIndices, sortPayReportingTableDataListIndices } from '../../common/utils';
import { TableSortId } from '../../common/types';
import { calculateSummary, getInputValidator } from '../utils';
import { DraftAlert } from '../components/DraftAlert';
import { links } from '../../router/links';

const itemsPerPage = 25;

export const PayrollInputPanel = () => {
  const dispatch = useAppDispatch();
  const { t } = useTypedTranslation();

  const {
    payReportingTableData,
    inputPayData: payData,
    isDraft,
    reportingBlockedByExpiringFok,
    payrollInputPanelState,
    reviewAndConfirmPayReportPanelState,
    tableUISettings: { showAlertIconLegend, showInactiveIconLegend },
    selectedPayMonthOverview,
  } = useAppSelector(state => state.payroll);

  const { payMonth, isPeriodOpen } = selectedPayMonthOverview ?? { payMonth: '', isPeriodOpen: null };

  const [showNoReportedRowsErrorMessage, setShowNoReportedRowsErrorMessage] = useState(false);
  const [showValidationErrorMessage, setShowValidationErrorMessage] = useState(false);
  const [validateOnRender, setValidateOnRender] = useState(false);
  const [tableSortState, setTableSortState] = useState<PayrollTableSortState>(
    initSortState(reportingBlockedByExpiringFok)
  );
  const [showInactiveEmployments, setShowInactiveEmployments] = useState(false);
  const searchQueryInput = useTextInputField('');
  const isMobile = useMediaMax(theme.breakpoint.md);

  useEffect(() => {
    if (showValidationErrorMessage && !Object.values(payData).some(val => val.hasError)) {
      setShowValidationErrorMessage(false);
    }
  }, [payData, showValidationErrorMessage]);

  useEffect(() => {
    if (showInactiveEmployments === true) {
      setTableSortState(prevState => ({
        ...defaultPayrollTableSortState,
        inactiveOrFokParameterChoices: HeadCellSortState.Asc,
      }));
    }
  }, [showInactiveEmployments]);

  useEffect(() => {
    if (showNoReportedRowsErrorMessage && Object.values(payData).some(val => val.payAmount !== null)) {
      setShowNoReportedRowsErrorMessage(false);
    }
  }, [payData, showNoReportedRowsErrorMessage]);

  const handleNextButtonClick = useCallback(() => {
    setValidateOnRender(true);

    const payDataWithErrors = Object.entries(payData).flatMap(([key, value]) => {
      const employment = payReportingTableData.find(each => each.policyNumber === key);

      const errors = getInputValidator({
        attributablePay: value.attributablePay,
        fokIsExpiring: employment?.activeMarkup?.fokIsExpiring,
        isPeriodOpen,
        personDeceased: !!employment?.employee.deathDate,
        reportedPayAmount: employment?.payAmountReported ?? null,
        isAbovePensionIncomeAge: !!employment?.isAbovePensionIncomeAge,
      })(value.payAmount?.toString() ?? '');
      return errors.length ? { policyNumber: value.policyNumber, error: true } : [];
    });

    if (payDataWithErrors.length > 0) {
      // Add hasError prop to (For sorting after error)
      dispatch(setPayInputErrors(payDataWithErrors));

      sortTable(TableSortId.HasError);
      setShowValidationErrorMessage(true);
      return;
    }

    const summary = calculateSummary(payReportingTableData, payData);

    if (summary.numberOfNewEntries === 0 && summary.numberOfChangedEntries === 0) {
      // If list is empty or is only inactive and no reported pays
      setShowNoReportedRowsErrorMessage(true);
    } else {
      // Proceed
      dispatch(setPayAmountSummaryData(summary));
      dispatch(goToReviewAndConfirm());
      trackCustomEvent('Manual reporting - proceeded to review and confirm', { payMonth: payMonth });
    }
  }, [payData, payReportingTableData, isPeriodOpen, dispatch, payMonth]);

  const goToPayrollInputPanel = () => {
    dispatch(setReviewAndConfirmChecked({ checked: false }));
    dispatch(goToPayrollInput());
  };

  const sortedAndFilteredListIndices = useMemo(() => {
    const filteredEmploymentsIndices = filterListIndices({
      employmentList: payReportingTableData,
      searchQuery: searchQueryInput.props.value,
      showTerminatedEmployments: showInactiveEmployments,
      showActiveEmployments: true,
    });
    return sortPayReportingTableDataListIndices({
      payReportingTableData: payReportingTableData,
      sortState: tableSortState,
      indices: filteredEmploymentsIndices,
      payrollData: payData,
    });
    // We want to control the sorting is triggers (and not have it re-sort when payroll data changes e.g.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payReportingTableData, tableSortState, searchQueryInput.props.value, showInactiveEmployments]);

  const { paginationProps, paginatedListIndices } = usePagination(sortedAndFilteredListIndices, itemsPerPage);

  const sortTable = (sortId: PayrollTableSortId) =>
    setTableSortState(state => {
      const newState = { ...defaultPayrollTableSortState };
      newState[sortId] = toggleSortStates(state[sortId]);
      return newState;
    });

  const clearFilters = () => {
    searchQueryInput.setValue('');
    setShowInactiveEmployments(false);
  };

  const numberOfInactiveEmployments = payReportingTableData.filter(x => x.isInactive).length;

  const totalAmount = showInactiveEmployments
    ? payReportingTableData.length
    : payReportingTableData.length - numberOfInactiveEmployments;

  const amountShowing = paginationProps.totalItems;

  if (!payReportingTableData) {
    return <EmployerMyPagesErrorPage startPageLinkUrl={links.payroll} />;
  }

  return (
    <Panel
      testId="test-panel-paytable"
      {...payrollInputPanelState}
      name="panel-paytable"
      onToggle={reviewAndConfirmPayReportPanelState.status === PanelStatus.Active ? goToPayrollInputPanel : undefined}
    >
      <Panel.Header marginTop="0px">
        {t('panel.payReport.panelTitle', {
          payMonth: getReadablePayMonth(payMonth),
        })}
      </Panel.Header>
      {isDraft && payrollInputPanelState.isOpen && <DraftAlert />}
      <Panel.Body>
        {payrollInputPanelState.isOpen && (
          <Panel.MainSection>
            <Flex marginBottom={28} justifyContent={'space-between'}>
              <Flex
                flexDirection={['column', 'row', 'row']}
                alignItems={['flex-start', 'center']}
                rowGap={20}
                columnGap={30}
                flexWrap={['no-wrap', 'wrap']}
                flexGrow={isMobile ? 1 : 0}
              >
                <Box minWidth={[335, 474]}>
                  <SearchInputField
                    isInitiallyMinified={isMobile}
                    searchQueryInput={searchQueryInput}
                    placeholder={isMobile ? t('common.searchShortLabel') : t('common.searchEmployeeLabel')}
                  />
                </Box>
                <Checkbox
                  id={'include-inactive-employments'}
                  checked={showInactiveEmployments}
                  onChange={() => setShowInactiveEmployments(!showInactiveEmployments)}
                >
                  {t('common.filter.includeInactiveEmployments')}
                </Checkbox>
              </Flex>
            </Flex>
            {amountShowing !== totalAmount && (
              <SearchFilterInfo
                amountShowing={amountShowing}
                totalAmount={totalAmount}
                clearFilter={clearFilters}
                marginTop={12}
              />
            )}
            {showInactiveEmployments && (
              <Feedback type="information" marginTop={30}>
                <Text marginBottom={6}>{t('infoMessages.payroll.inactiveEmployments')}</Text>
                <Link to={links.employments}>
                  {t('infoMessages.payroll.inactiveEmploymentsLink', {
                    employmentsServiceName: t('common.serviceNames.employments'),
                  })}
                </Link>
              </Feedback>
            )}

            <Container overflowX={'auto'} className="notranslate">
              <PayrollReportingTable
                payReportingTableData={payReportingTableData}
                listIndices={paginatedListIndices}
                sortTable={sortTable}
                sortState={tableSortState}
                showIconColumn={showAlertIconLegend || showInactiveIconLegend}
                validateOnRender={validateOnRender}
              />
            </Container>
            <Pagination {...paginationProps} />
            {(showValidationErrorMessage || showNoReportedRowsErrorMessage) && !reportingBlockedByExpiringFok && (
              <Feedback size="slim" type="error">
                {t(
                  showNoReportedRowsErrorMessage
                    ? 'errorMessages.noReportedRows'
                    : 'errorMessages.inputValidationErrors'
                )}
              </Feedback>
            )}
            {reportingBlockedByExpiringFok && (
              <Feedback size="slim" type="error">
                {t('errorMessages.expiringFok')}
              </Feedback>
            )}
            <Button onClick={handleNextButtonClick} style={{ marginTop: '4rem' }}>
              {t('common.nextStep')}
            </Button>
          </Panel.MainSection>
        )}
      </Panel.Body>
    </Panel>
  );
};

const initSortState = (reportingBlockedByExpiringFok: boolean): PayrollTableSortState => {
  if (reportingBlockedByExpiringFok) {
    return {
      ...defaultPayrollTableSortState,
      hasError: HeadCellSortState.Asc,
    };
  }
  return {
    ...defaultPayrollTableSortState,
    employeeId: HeadCellSortState.Asc,
  };
};
